class RemoteDataTable extends HTMLElement {
  constructor() {
    super();
    this.data = [];
    this.loading = false;
    this.error = null;
    this.contractorId = this.getAttribute('contractor-id');
    this.endpoint = this.getAttribute('endpoint') || 'https://fy6n2l5e7e.execute-api.us-east-2.amazonaws.com/prod/quotes';
  }

  connectedCallback() {
    this.render();
    if (this.contractorId) {
      this.fetchData();
    } else {
      this.showError('Contractor ID is required');
    }
  }

  static get observedAttributes() {
    return ['contractor-id', 'endpoint'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'contractor-id' && newValue !== oldValue) {
      this.contractorId = newValue;
      if (this.contractorId) {
        this.fetchData();
      }
    }
    if (name === 'endpoint' && newValue !== oldValue) {
      this.endpoint = newValue;
    }
  }

  async fetchData() {
    if (!this.contractorId) return;

    this.loading = true;
    this.error = null;
    this.render();

    try {
      const url = new URL(this.endpoint);
      url.searchParams.append('contractor_id', this.contractorId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log("response: ", response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      this.data = this.sortByMostRecentDate(result.quotes || result.data || result);
      console.log('Fetched data:', this.data);
      this.error = null;
    } catch (err) {
      console.error('Error fetching data:', err);
      this.error = err.message;
      this.data = [];
    } finally {
      this.loading = false;
      this.render();
    }
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="remote-data-table loading">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p>Loading quotes...</p>
          </div>
        </div>
      `;
      return;
    }

    if (this.error) {
      this.innerHTML = `
        <div class="remote-data-table error">
          <div class="error-message">
            <h3>Error Loading Data</h3>
            <p>${this.error}</p>
            <button class="retry-button" onclick="this.closest('remote-data-table').fetchData()">
              Try Again
            </button>
          </div>
        </div>
      `;
      return;
    }

    if (!this.data || this.data.length === 0) {
      this.innerHTML = `
        <div class="remote-data-table empty">
          <div class="empty-state">
            <h3>No Quotes Found</h3>
            <p>No quotes are available for this contractor.</p>
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="remote-data-table">
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                ${this.renderTableHeaders()}
              </tr>
            </thead>
            <tbody>
              ${this.renderTableRows()}
            </tbody>
          </table>
        </div>
        <div class="table-footer">
          <p class="table-info">Showing ${this.data.length} quote${this.data.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
    `;
  }

  renderTableHeaders() {
    const columnsConfig = this.getAttribute('columns');
    let columns;

    if (columnsConfig) {
      try {
        columns = JSON.parse(columnsConfig);
      } catch (e) {
        console.warn('Invalid columns configuration, using defaults', e.message);
        columns = this.getDefaultColumns();
      }
    } else {
      columns = this.getDefaultColumns();
    }

    return columns.map(col => `<th>${col.label}</th>`).join('');
  }

  getDefaultColumns() {
    return [
      { key: 'address', label: 'Address' },
      { key: 'updatedAt', label: 'Last Updated', type: 'date' },
      { key: 'actions', label: '' }
    ];
  }

  renderTableRows() {
    const columns = this.getColumns();

    return this.data.map((item, index) => `
      <tr>
        ${columns.map(col => `<td>${this.renderCell(item, col)}</td>`).join('')}
      </tr>
    `).join('');
  }

  getColumns() {
    const columnsConfig = this.getAttribute('columns');
    if (columnsConfig) {
      try {
        return JSON.parse(columnsConfig);
      } catch (e) {
        console.warn('Invalid columns configuration, using defaults');
      }
    }
    return this.getDefaultColumns();
  }

  renderCell(item, column) {
    if (column.key === 'actions') {
      return this.renderActions(item);
    }

    const value = this.getNestedValue(item, column.key);

    if (column.type === 'date') {
      return this.formatDate(value);
    }

    if (column.type === 'currency') {
      return this.formatCurrency(value);
    }

    if (column.type === 'status') {
      return `<span class="status-badge status-${(value || 'pending').toLowerCase()}">
        ${this.capitalizeFirst(value || 'Pending')}
      </span>`;
    }

    if (column.formatter && typeof column.formatter === 'function') {
      return column.formatter(value, item);
    }

    return value || 'N/A';
  }

  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  renderActions(item) {
    const quoteId = item.id || item.quote_id;
    return `
      <div class="action-buttons">
        <button 
          class="button button--sm" 
          onclick="this.closest('remote-data-table').viewQuote('${quoteId}')"
        >
          View
        </button>
      </div>
    `;
  }

  formatDate(dateString) {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  formatCurrency(amount) {
    if (!amount) return 'N/A';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numAmount);
  }

  sortByMostRecentDate(items) {
    if (!Array.isArray(items)) return [];

    const getTimestamp = (item) => {
      const dateValue = item?.updatedAt || item?.updated_at || item?.modifiedAt || item?.createdAt || item?.created_at;
      const timestamp = new Date(dateValue).getTime();
      return Number.isFinite(timestamp) ? timestamp : -Infinity;
    };

    return [...items].sort((a, b) => getTimestamp(b) - getTimestamp(a));
  }

  capitalizeFirst(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  viewQuote(quoteId) {
    this.dispatchEvent(new CustomEvent('quote-view', {
      detail: { quoteId },
      bubbles: true
    }));
  }

  downloadQuote(quoteId) {
    this.dispatchEvent(new CustomEvent('quote-download', {
      detail: { quoteId },
      bubbles: true
    }));
  }

  showError(message) {
    this.error = message;
    this.loading = false;
    this.render();
  }

  // Public method to refresh data
  refresh() {
    this.fetchData();
  }
}

// Register the custom element
customElements.define('remote-data-table', RemoteDataTable);
